// Shared by the employee preview and Express's public HTML renderer.
const React = require('react');
const MarkdownIt = require('markdown-it');
const h = React.createElement;
const markdown = new MarkdownIt({ html: false, breaks: true, linkify: false });
function safeLink(value) {
    return /^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(value) && !Array.from(value).some(character => character.charCodeAt(0) <= 32 || character === '\\');
}
markdown.validateLink = safeLink;
for (const rule of ['heading_open', 'heading_close']) {
    markdown.renderer.rules[rule] = (tokens, index, options, env, self) => {
        if (tokens[index].tag === 'h1') tokens[index].tag = 'h2';
        return self.renderToken(tokens, index, options);
    };
}
function readableDate(value) {
    const date = new Date(value);
    return value && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-US', { dateStyle: 'medium', timeZone: 'UTC' }) : '';
}
function ImageMarkup({ src, alt, eager = false }) {
    return src && /^https?:\/\//i.test(src) ? h('img', { src, alt: alt || '', loading: eager ? 'eager' : 'lazy', decoding: 'async' }) : null;
}
// Split only at top-level block boundaries: never cut a list, quote, or code block.
function articleBody(content) {
    const tokens = markdown.parse(content || '', {});
    const blocks = [];
    let pending = [];
    for (const token of tokens) {
        pending.push(token);
        if (token.level === 0 && token.nesting !== 1) {
            const marker = pending.length === 3 && pending[0].type === 'paragraph_open' && pending[1].content.match(/^\[\[image:([23])\]\]$/);
            blocks.push({ tokens: pending, image: marker ? Number(marker[1]) : null, heading: pending[0].type === 'heading_open' });
            pending = [];
        }
    }
    return blocks;
}
function ArticleMarkup({ post, ImageComponent = ImageMarkup }) {
    const published = post.firstPublishedAt || post.date;
    const updated = post.firstPublishedAt && readableDate(post.updatedAt) && new Date(post.updatedAt) > new Date(post.firstPublishedAt) && readableDate(post.updatedAt) !== readableDate(post.firstPublishedAt);
    const blocks = articleBody(post.content);
    const image = (number, eager = false) => /^https?:\/\//i.test(post[`img${number}`] || '') ? h('figure', { key: `image-${number}`, className: eager ? 'blog-figure blog-hero' : 'blog-figure' },
        h(ImageComponent, { src: post[`img${number}`], alt: post[`img${number}Alt`], eager })) : null;
    const explicit = new Set(blocks.filter(block => block.image).map(block => block.image));
    const boundaries = blocks.map((block, index) => block.heading && index > 0 ? index : null).filter(index => index !== null);
    const fallback = boundaries.length ? boundaries : blocks.map((_, index) => index).filter(index => index > 0);
    const placements = new Map();
    [2, 3].filter(number => !explicit.has(number) && post[`img${number}`]).forEach((number, index, images) => {
        if (!fallback.length) return;
        const target = fallback[Math.min(fallback.length - 1, Math.floor(fallback.length * (index + 1) / (images.length + 1)))];
        placements.set(target, [...(placements.get(target) || []), number]);
    });
    const rendered = new Set();
    const insert = number => { if (rendered.has(number)) return null; rendered.add(number); return image(number); };
    const body = [];
    let group = [];
    const flush = () => {
        if (group.length) body.push(h('div', { key: `text-${body.length}`, className: 'blog-markdown', dangerouslySetInnerHTML: { __html: markdown.renderer.render(group, markdown.options, {}) } }));
        group = [];
    };
    blocks.forEach((block, index) => {
        const images = placements.get(index) || [];
        if (images.length || block.image) { flush(); images.forEach(number => body.push(insert(number))); }
        if (block.image) body.push(insert(block.image));
        else group.push(...block.tokens);
    });
    flush();
    [2, 3].forEach(number => { if (!rendered.has(number)) body.push(insert(number)); });
    const minutes = Math.max(1, Math.ceil((post.content || '').split(/\s+/).filter(Boolean).length / 220));
    return h('article', { className: 'blog-article' },
        h('header', { className: 'blog-article-header' },
            h('p', { className: 'blog-eyebrow' }, 'Blake Technologies · Insights'),
            h('h1', null, post.title || 'Untitled draft'),
            post.subtitle && h('p', { className: 'subtitle' }, post.subtitle),
            h('div', { className: 'blog-article-meta' },
                h('p', null, post.author ? `Written by ${post.author}` : 'Author not yet added'),
                readableDate(published) && h('p', null, post.firstPublishedAt ? 'Published: ' : 'Article date: ', h('time', { dateTime: new Date(published).toISOString() }, readableDate(published))),
                updated && h('p', null, 'Updated: ', h('time', { dateTime: new Date(post.updatedAt).toISOString() }, readableDate(post.updatedAt))),
                h('p', null, `${minutes} min read`))),
        image(1, true),
        h('div', { className: 'blog-article-body' }, body)
    );
}
module.exports = { ArticleMarkup, ImageMarkup, safeLink, readableDate };
